import React from 'react';
import { Alert } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import type LabEnvironmentStore from '../../store';

type AlertType =
  | 'generic'
  | 'capacity_exceeded'
  | 'lab_offering_not_allowed'
  | 'throttled'
  | 'user_throttled'
  | 'lab_quota_limit_reached';

type LabAlertProps = {
  alertType?: AlertType | string;
  labStore: LabEnvironmentStore;
};

const LabAlert: React.FC<LabAlertProps> = ({
  alertType = 'generic',
  labStore,
}) => {
  const { t } = useTranslation();

  const alerts: Record<
    AlertType,
    {
      variant: 'warning';
      message: string;
      displayInState: string[];
    }
  > = {
    generic: {
      variant: 'warning',
      message: t(
        'We are not able to start your lab at this time.  Please try back again later.',
      ),
      displayInState: ['ready', 'deleted'],
    },
    capacity_exceeded: {
      variant: 'warning',
      message: t(
        'We are not able to start your lab at this time, our lab capacity in your region has reached the maximum number of users.  Please try back again later.',
      ),
      displayInState: ['ready', 'deleted'],
    },
    lab_offering_not_allowed: {
      variant: 'warning',
      message: t('Labs for this course are not available for you.'),
      displayInState: ['ready', 'deleted'],
    },
    throttled: {
      variant: 'warning',
      message: t(
        'We are not able to start your lab at this time, our lab capacity in your region has reached the maximum number of users.  Please try back again later.',
      ),
      displayInState: ['ready', 'deleted', 'stopped'],
    },
    user_throttled: {
      variant: 'warning',
      message: t(
        'We are not able to start your lab as you reached the maximum number of running labs per user. Please stop or delete a running lab to start this lab.',
      ),
      displayInState: ['ready', 'deleted', 'stopped'],
    },
    lab_quota_limit_reached: {
      variant: 'warning',
      message: t(
        'You have reached your lab usage limit and no further labs will be able to be started during this subscription period.',
      ),
      displayInState: ['ready', 'deleted', 'stopped'],
    },
  };

  const selectedAlert =
    alertType in alerts ? alerts[alertType as AlertType] : alerts.generic;

  if (!selectedAlert.displayInState.includes(labStore.labState)) {
    return null;
  }

  return (
    <Alert
      isInline
      variant={selectedAlert.variant}
      title={selectedAlert.message}
    />
  );
};

export default observer(LabAlert);
